/**
 * NoPhotoFallback — universal canvas / color tile that derives a
 * deterministic initial-letter avatar from the display name.
 *
 * Per §47 of the Bucket-B briefing, photos are optional and every
 * surface that needs an avatar/card image must support NO_PHOTO.
 *
 * The color is derived from a stable hash of the name so the same
 * user always sees the same color across surfaces (deterministic,
 * identity-minimized). No actual photo is rendered.
 */

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return h;
}

const PALETTE: readonly string[] = Object.freeze([
  '#3ec9b0', '#6366f1', '#f59e0b', '#ec4899', '#22c55e',
  '#0ea5e9', '#a855f7', '#ef4444', '#14b8a6', '#f97316',
]);

function pickColor(name: string): string {
  const idx = hashName(name) % PALETTE.length;
  // PALETTE.length is non-zero and stable; assert defensively.
  return PALETTE[idx] ?? '#3ec9b0';
}

function firstInitial(name: string): string {
  // C6: Latin initial (not Cyrillic 'А') — same convention as the
  // existing my-profile view.
  const ch = name.charAt(0).toUpperCase();
  return ch || '·';
}

export function NoPhotoFallback({
  name,
  size = 96,
}: {
  readonly name: string;
  readonly size?: number;
}): React.ReactNode {
  const color = pickColor(name);
  const initial = firstInitial(name);
  return (
    <div
      className="pd-no-photo"
      role="img"
      aria-label={`Аватар для ${name}`}
      data-testid="profile-detail-no-photo"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: color,
        color: 'white',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.round(size * 0.42)}px`,
        fontWeight: 600,
      }}
    >
      {initial}
    </div>
  );
}